import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'

import { Button, DatePicker, Form, Input, InputNumber, Spin, Table } from 'antd'
import locale from 'antd/es/date-picker/locale/ru_RU'

import { userSelector } from '../../../store/session'
import HighlighterWrapper from '../../HighlighterWrapper'
import { formatMoney } from '../../../utils/number-utils'
import { dateRange, formatDate } from '../../../utils/date-format'
import useTableFilter from '../../hooks/useTableFilter'
import ProjectSelector from '../../ProjectSelector'
import { getWebSearchReportByProject } from '../../../services/web-search'
import { comparePrice, getCellOverStyle } from '../../WebSearch/web-search-utils'
import exportToXLS from './exportToXLS'

const { RangePicker } = DatePicker

const DEFAULT_HEIGHT = window.innerHeight || 600
const DATE_FORMAT = 'DD.MM.YYYY'

const FIELD_FILTERS = {
  estimateFieldName: (record, value) =>
    _.get(record, 'estimateFieldName')?.toString().toLowerCase().includes(value.toLowerCase()),
}

const filterData = (data, filters) => {
  const f = Object.entries(filters)
    .filter(([key, value]) => value && FIELD_FILTERS[key])
    .map(
      ([key, value]) =>
        (record) =>
          FIELD_FILTERS[key](record, value)
    )

  return f.length > 0 ? data.filter((row) => f.every((filter) => filter(row))) : data
}

const buildFilter = ({ project, application, appDates, priceFrom, priceTo }) => ({
  project,
  application,
  createdAt: dateRange(appDates),
  price: { from: priceFrom, to: priceTo },
})

const buildHierarchicalReport = (rows) => {
  if (!rows || rows.length === 0) {
    return []
  }

  // Группируем данные для подсчёта span
  const groupedByApp = _.groupBy(rows, 'appId')

  // Считаем appRowSpan для каждого appId (всего записей для appId)
  const appRowSpans = _.mapValues(groupedByApp, (appRows) => appRows.length)

  // Считаем appItemRowSpan для каждого appItemId внутри каждого appId
  const appItemRowSpans = {}

  _.forEach(groupedByApp, (appRows, appId) => {
    const groupedByAppItem = _.groupBy(appRows, 'appItemId')
    appItemRowSpans[appId] = _.mapValues(groupedByAppItem, (itemRows) => itemRows.length)
  })

  // Отслеживаем, какие appId и appItemId уже встречались
  const seenApps = new Set()
  const seenAppItems = new Set()

  // Формируем результат
  return rows.map((row) => {
    const result = { ...row }
    const appKey = row.appId
    const appItemKey = `${row.appId}-${row.appItemId}`

    // Добавляем appRowSpan только для первой записи appId
    if (!seenApps.has(appKey)) {
      result.appRowSpan = appRowSpans[appKey]
      seenApps.add(appKey)
    }

    // Добавляем appItemRowSpan только для первой записи appItemId
    if (!seenAppItems.has(appItemKey)) {
      result.appItemRowSpan = appItemRowSpans[appKey][row.appItemId]
      seenAppItems.add(appItemKey)
    }

    return result
  })
}

const numberCellStyle = {
  padding: 8,
  height: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const WebSearchReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [xScrollEnabled, setXScrollEnabled] = useState(false)
  const [filter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()
  const [data, setData] = useState([])
  const [project, setProject] = useState(null)

  const { user } = useSelector(userSelector)

  const [form] = Form.useForm()

  const columns = [
    {
      title: 'Заявка',
      children: [
        {
          title: 'Проект',
          key: 'project',
          dataIndex: 'projectName',
          align: 'left',
          width: 150,
          onCell: (r) => (r.appRowSpan ? { rowSpan: r.appRowSpan } : { rowSpan: 0 }),
        },
        {
          title: 'Ид заявки',
          key: 'application-id',
          dataIndex: 'appId',
          align: 'right',
          width: 80,
          onCell: (r) => (r.appRowSpan ? { rowSpan: r.appRowSpan } : { rowSpan: 0 }),
          render: (application_id) => (
            <a href={`/applications?state=all&id=${application_id}`} target="_blank" rel="noreferrer">
              {application_id}
            </a>
          ),
        },
        {
          title: 'Дата заявки',
          key: 'application-date',
          dataIndex: 'appCreateDate',
          align: 'center',
          width: 100,
          onCell: (r) => (r.appRowSpan ? { rowSpan: r.appRowSpan } : { rowSpan: 0 }),
          render: formatDate,
        },
      ],
    },
    {
      title: 'Смета',
      children: [
        {
          title: 'Наименование из сметы',
          key: 'estimate-field-name',
          dataIndex: 'estimateFieldName',
          align: 'left',
          width: 350,
          ...getColumnSearchProps('estimateFieldName'),
          onCell: (r) => (r.appItemRowSpan ? { rowSpan: r.appItemRowSpan } : { rowSpan: 0 }),
          render: (value) =>
            searchedColumn === 'estimateFieldName' ? (
              <HighlighterWrapper searchWords={[searchText]} textToHighlight={value ? value.toString() : ''} />
            ) : (
              value
            ),
        },
        {
          title: 'Количество',
          key: 'app-item-amount',
          dataIndex: 'appItemAmount',
          align: 'right',
          width: 90,
          onCell: (r) => (r.appItemRowSpan ? { rowSpan: r.appItemRowSpan } : { rowSpan: 0 }),
        },
        {
          title: 'Ед. измерения',
          key: 'estimate-field-unit',
          dataIndex: 'estimateFieldUnit',
          align: 'left',
          width: 90,
          onCell: (r) => (r.appItemRowSpan ? { rowSpan: r.appItemRowSpan } : { rowSpan: 0 }),
        },
        {
          title: 'Цена из сметы',
          key: 'estimate-field-price',
          dataIndex: 'estimateFieldPrice',
          align: 'right',
          width: 100,
          onCell: (r) => (r.appItemRowSpan ? { rowSpan: r.appItemRowSpan } : { rowSpan: 0 }),
          render: (value) => formatMoney(value),
        },
      ],
    },
    {
      title: 'Поиск',
      children: [
        {
          title: 'Наименование продукта',
          key: 'web-search-field-name',
          dataIndex: 'webSearchFieldProductName',
          align: 'left',
          width: 350,
        },
        {
          title: () => <div style={{ padding: 8 }}>Актуальная цена</div>,
          key: 'web-search-field-price',
          dataIndex: 'webSearchFieldPrice',
          className: 'no-padding-cell-report',
          align: 'right',
          width: 100,
          render: (price, record) => {
            const over = comparePrice(record.estimateFieldPrice, price)
            return <div style={{ ...numberCellStyle, ...getCellOverStyle(over) }}>{formatMoney(price)}</div>
          },
        },
        {
          title: 'Продавец',
          key: 'web-search-field-source',
          dataIndex: 'webSearchFieldSource',
          align: 'left',
          width: 150,
        },
        {
          title: 'Ссылка на товар',
          key: 'web-search-field-url',
          dataIndex: 'webSearchFieldUrl',
          align: 'left',
          width: 500,
          render: (value) => (
            <a href={value} target="_blank" rel="noreferrer">
              {value}
            </a>
          ),
        },
      ],
    },
  ]

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      setXScrollEnabled(true)
    }
  }, [isLoading, data])

  const findProjectById = useCallback(
    (projectId) => user?.projects?.find(({ id }) => id === projectId)?.name ?? null,
    [user]
  )

  const onFormSubmit = useCallback((values) => {
    setIsLoading(true)
    setProject(values.project)

    const filter = buildFilter(values)
    getWebSearchReportByProject(filter)
      .then((response) => {
        const results = response?.data
        if (results) {
          setData(results)
        }
      })
      .catch(() => {
        console.warn('Loading report result error')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const onClear = useCallback(() => form.resetFields(), [form])

  const exportData = useCallback(() => {
    const projectName = findProjectById(project)
    exportToXLS(transformed, projectName, 'Отчет_по_web_поиску.xlsx')
  }, [data, filter, user, project])

  const enrichData = useCallback(
    (data) => {
      return data.map((row) => ({
        ...row,
        projectName: findProjectById(row.projectId),
      }))
    },
    [data]
  )

  const transformed = useMemo(() => {
    const filtered = filterData(data, filter)
    const sortedData = _.sortBy(filtered, ['appId', 'appItemId', 'webSearchFieldId'])
    const spawned = buildHierarchicalReport(sortedData)
    return enrichData(spawned)
  }, [data, filter])

  useEffect(() => {
    if (!isLoading && data && data.length > 0) {
      setXScrollEnabled(true)
    }
  }, [isLoading, data])

  return (
    <Spin spinning={isLoading}>
      <Form initialValues={{}} form={form} layout="vertical" style={{ display: 'flex' }} onFinish={onFormSubmit}>
        <Form.Item style={{ width: 300, maxWidth: 300 }} label="Проект" name="project">
          <ProjectSelector />
        </Form.Item>
        <Form.Item style={{ marginLeft: 15, width: 80 }} label="Заявка" name="application">
          <Input allowClear />
        </Form.Item>
        <Form.Item style={{ marginLeft: 15, maxWidth: 120, minWidth: 100 }} label="Цена от" name="priceFrom">
          <InputNumber allowClear />
        </Form.Item>
        <Form.Item style={{ maxWidth: 120, minWidth: 100 }} label="Цена до" name="priceTo">
          <InputNumber allowClear />
        </Form.Item>
        <Form.Item label="Даты заявки" style={{ marginLeft: 15, maxWidth: 240 }} name="appDates">
          <RangePicker locale={locale} format={DATE_FORMAT} />
        </Form.Item>
        <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
          <Button onClick={() => form.submit()}>Сформировать отчет</Button>
        </Form.Item>
        <Button style={{ marginLeft: 15, marginTop: 30 }} disabled={data.length === 0} onClick={exportData}>
          Скачать
        </Button>
        <Form.Item label="" style={{ marginLeft: 15, marginTop: 30 }}>
          <Button type="dashed" onClick={onClear}>
            Очистить
          </Button>
        </Form.Item>
      </Form>
      <div style={{ paddingTop: 10 }}>
        <Table
          id="webSearchReportTable"
          className="table-web-search"
          columns={columns}
          dataSource={transformed}
          bordered
          size="small"
          pagination={false}
          scroll={{ x: xScrollEnabled ? 1800 : undefined, y: DEFAULT_HEIGHT - 240 }}
        />
      </div>
    </Spin>
  )
}

export default WebSearchReport
